<template>
  <div :class="{ 'is-minimized': minimized }" class="app-menu">
    <router-link class="app-menu__item" :class="{ selected: isInboxMode }" :to="TendersModes.INBOX.url">
      <BaseIcon class="app-menu__icon" name="home" />
      <span class="app-menu__text">{{ $t('menu.inbox') }}</span>
    </router-link>

    <span class="sidebar__divider"></span>

    <router-link class="app-menu__item" :class="{ selected: isExploreMode }" :to="paths.explore">
      <BaseIcon class="app-menu__icon" name="search" />
      <span class="app-menu__text">{{ $t('menu.explore') }}</span>
    </router-link>

    <span class="sidebar__divider"></span>

    <router-link
      class="app-menu__item"
      :class="{ selected: isOpportunitiesMode }"
      :to="TendersModes.OPPORTUNITIES.url"
      v-if="tenantHasOpportunitiesAccess()"
    >
      <BaseIcon class="app-menu__icon" name="bullseye-arrow" />
      <span class="app-menu__text">{{ $t('menu.opportunities') }}</span>
    </router-link>

    <template v-if="hasOtherToolsSection">
      <div class="app-menu__section--subtitle">{{ $t('menu-subtitles.other-tools') }}</div>

      <router-link
        class="app-menu__item"
        v-if="tenantHasContactDirectoryAccess()"
        :class="{ selected: isContactDirectoryMode }"
        :to="paths.contactDirectory"
      >
        <BaseIcon class="app-menu__icon" name="account-search-outline" />
        <span class="app-menu__text">{{ $t('menu.contact-directory') }}</span>
      </router-link>

      <span
        class="sidebar__divider"
        v-if="tenantHasContactDirectoryAccess() && (tenantHasBusinessIntelligenceAccess() || showBiAsLocked)"
      ></span>

      <router-link
        active-class="app-menu__item selected"
        class="app-menu__item"
        :to="paths.businessIntelligence"
        :class="{ selected: isBusinessIntelligenceMode, 'app-menu__item--locked': showBiAsLocked }"
        v-if="tenantHasBusinessIntelligenceAccess() || showBiAsLocked"
      >
        <span class="app-menu__icon-wrap">
          <BaseIcon class="app-menu__icon" name="trending-up" />
          <span
            v-if="showBiAsLocked"
            class="app-menu__lock"
            :title="$t('upsell.addon-label')"
            :aria-label="$t('upsell.addon-label')"
          >
            <BaseIcon name="lock" />
          </span>
        </span>
        <span class="app-menu__text">{{ $t('menu.business-intelligence') }}</span>
      </router-link>

      <span
        class="sidebar__divider"
        v-if="
          (tenantHasPreProjectsAccess() || showPreProjectsAsLocked) &&
          (tenantHasContactDirectoryAccess() || tenantHasBusinessIntelligenceAccess() || showBiAsLocked)
        "
      ></span>
      <router-link
        class="app-menu__item"
        v-if="tenantHasPreProjectsAccess() || showPreProjectsAsLocked"
        :class="{ selected: isPreProjectsActive, 'app-menu__item--locked': showPreProjectsAsLocked }"
        :to="paths.preProjects"
      >
        <span class="app-menu__icon-wrap">
          <BaseIcon class="app-menu__icon" name="telescope" />
          <span
            v-if="showPreProjectsAsLocked"
            class="app-menu__lock"
            :title="$t('upsell.addon-label')"
            :aria-label="$t('upsell.addon-label')"
          >
            <BaseIcon name="lock" />
          </span>
        </span>
        <span class="app-menu__text">{{ $t('menu.pre-projects') }}</span>
      </router-link>
    </template>

    <span class="sidebar__divider" v-if="tenantHasOpportunitiesAccess() || hasOtherToolsSection"></span>

    <!-- Phones open the settings on their list; larger screens on the profile, beside the list. -->
    <router-link
      class="app-menu__item"
      :class="{ selected: isAdminActive }"
      :to="isPhone ? paths.admin : paths.adminUserProfile"
    >
      <BaseIcon class="app-menu__icon" name="settings" />
      <span class="app-menu__text">{{ $t('menu.settings') }}</span>
    </router-link>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon.vue';
import {
  tenantHasBusinessIntelligenceAccess,
  tenantHasContactDirectoryAccess,
  tenantHasOpportunitiesAccess,
  tenantHasPreProjectsAccess,
} from '@/util/module-access-util';
import { shouldShowUpsellSurfaces } from '@/util/tier-mapping';
import { mapGetters } from 'vuex';
import { Modes } from '@/models/modes';
import { paths } from '@/util/paths';
import { viewport } from '@/util/viewport';

export default {
  name: 'SidebarMenu',
  components: {
    BaseIcon,
  },
  data() {
    return {
      TendersModes: Modes,
    };
  },
  props: {
    minimized: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    paths() {
      return paths;
    },
    ...mapGetters('modes', ['mode']),
    isPhone() {
      return viewport.size === 'phone';
    },
    isAdminActive() {
      return this.$route.matched.some((route) => route.path.includes('/admin'));
    },
    isPreProjectsActive() {
      return this.$route.path.startsWith(paths.preProjects);
    },
    isExploreMode() {
      return this.mode === Modes.EXPLORE.name;
    },
    isContactDirectoryMode() {
      return this.mode === Modes.CONTACT_DIRECTORY.name;
    },
    isInboxMode() {
      // Keywords is reached from the inbox header (and its back arrow returns there), so treat it
      // as a sub-page of the inbox — keep Inbox highlighted rather than leaving nothing selected.
      return this.mode === Modes.INBOX.name || this.$route.name === 'keywords';
    },
    isOpportunitiesMode() {
      return this.mode === Modes.OPPORTUNITIES.name;
    },
    isBusinessIntelligenceMode() {
      return this.mode === Modes.BUSINESS_INTELLIGENCE.name;
    },
    hasOtherToolsSection() {
      return (
        this.tenantHasContactDirectoryAccess() ||
        this.tenantHasBusinessIntelligenceAccess() ||
        this.tenantHasPreProjectsAccess() ||
        this.showBiAsLocked ||
        this.showPreProjectsAsLocked
      );
    },
    showBiAsLocked() {
      return !this.tenantHasBusinessIntelligenceAccess() && shouldShowUpsellSurfaces();
    },
    showPreProjectsAsLocked() {
      return !this.tenantHasPreProjectsAccess() && shouldShowUpsellSurfaces();
    },
  },
  methods: {
    tenantHasBusinessIntelligenceAccess,
    tenantHasOpportunitiesAccess,
    tenantHasContactDirectoryAccess,
    tenantHasPreProjectsAccess,
  },
};
</script>

<style lang="scss">
.app-menu {
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 24px;

  &__item {
    align-items: center;
    color: $color-normal;
    cursor: pointer;
    display: flex;
    font-family: $font-family-raleway;
    font-size: $font-size-small;
    font-weight: bold;
    position: relative;
    text-align: left;
    text-decoration: none;
    width: 100%;
    height: $sidebar-item-height;
    padding: 24px;

    &.selected {
      background: $color-accent-100;
      color: $color-accent !important;

      &:before {
        background: $color-accent;
        content: '';
        display: block;
        height: $sidebar-item-height;
        position: absolute;
        left: 0;
        width: 4px;
      }

      &:hover {
        background: $color-accent-100;
      }
    }

    &:hover {
      background: $color-hover;
    }

    &:focus {
      box-shadow: $shadow-focus-outline;
    }

    &[disabled] {
      cursor: not-allowed;
      opacity: 0.4;
    }

    + .app-menu__item {
      height: $sidebar-item-height;
    }
  }

  &__section {
    &--subtitle {
      color: $color-bluey-gray;
      font-size: $font-size-xsmall;
      text-transform: uppercase;
      padding-left: 24px;
      transition: opacity 200ms ease-in-out 0.15s; //opening
      margin-top: 16px;
      height: 24px;
      align-content: center;
      width: 100%;
    }
  }

  &__icon {
    flex: none;
    width: 20px;
  }

  &__icon-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 20px;
    height: 20px;
  }

  &__lock {
    position: absolute;
    bottom: -6px;
    right: -6px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: $color-accent-500;
    color: white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.2);

    .base-icon {
      width: 8px !important;
      height: 8px !important;
      display: flex;
    }
  }

  &__item--locked {
    .app-menu__text {
      color: $color-normal;
    }
  }

  &__text {
    display: flex;
    align-items: center;
    margin-left: 11px;
    white-space: nowrap;
    opacity: 1;
    visibility: visible;
    transition: opacity 200ms ease-in-out 0.1s; //opening menu__text
  }

  &.is-minimized {
    .app-menu__text {
      opacity: 0;
      visibility: hidden;
      transition: opacity 150ms ease-in-out; //closing menu__text
    }

    .app-menu__section--subtitle {
      opacity: 0;
      transition: opacity 150ms ease-in-out; //closing menu__text
    }
  }
}

.sidebar {
  &__divider {
    background: $color-neutral-pale;
    display: block;
    height: 2px;
    width: 100%;
  }
}
</style>
